import { Button, Code, Select, Stack, Text } from "@mantine/core";
import { useEffect, useState } from "react";
import {
  EmptyState,
  ErrorState,
  LoadingSkeleton,
  RecordCard,
  SectionCard,
} from "../../components/ui";
import { api, type AuditEvent } from "../../lib/api";
import { Page } from "../../shell/Page";

/**
 * The audit trail (REQ-ADM-1 / REQ-NFR-5).
 *
 * Read-only, and not by convention: audit_events rejects UPDATE and DELETE at
 * the database, so there is nothing to offer beyond reading. The payload is
 * shown as stored rather than summarised, because the question this screen
 * answers is usually "what exactly changed?" and a summary is where that
 * detail goes to die.
 */

/** Payload arrives as JSON text - SQL Server has no Json column type. */
function formatPayload(payload: string | null): string | undefined {
  if (!payload) return undefined;
  try {
    return JSON.stringify(JSON.parse(payload), null, 2);
  } catch {
    // Malformed is still worth showing: it is what the trail actually holds.
    return payload;
  }
}

function actionLabel(action: string): string {
  return action.toLowerCase().replace(/_/g, " ");
}

export function AuditLog() {
  const [events, setEvents] = useState<AuditEvent[]>();
  const [actions, setActions] = useState<string[]>([]);
  const [action, setAction] = useState("");
  const [entityType, setEntityType] = useState("");
  const [openId, setOpenId] = useState<string>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    void (async () => {
      try {
        const meta = await api.adminMeta();
        setActions(meta.auditActions);
      } catch {
        // The filter degrades to "everything"; the list below still loads.
      }
    })();
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        setError(undefined);
        const { events: loaded } = await api.auditEvents({
          ...(action ? { action } : {}),
          ...(entityType ? { entityType } : {}),
        });
        setEvents(loaded);
      } catch (caught) {
        setError(
          caught instanceof Error ? caught.message : "Could not load the audit trail.",
        );
      }
    })();
  }, [action, entityType]);

  const entityTypes = Array.from(
    new Set((events ?? []).map((event) => event.entityType)),
  ).sort();

  return (
    <Page
      title="Audit trail"
      subtitle={events ? `${events.length} most recent` : undefined}
      back="/admin"
      width="wide"
    >
      {error && <ErrorState message={error} />}

      <SectionCard title="Filter">
        <Stack gap="sm">
          <Select
            id="audit-action"
            label="Action"
            value={action}
            onChange={(value) => setAction(value ?? "")}
            allowDeselect={false}
            searchable
            data={[
              { value: "", label: "Every action" },
              ...actions.map((value) => ({ value, label: actionLabel(value) })),
            ]}
          />
          <Select
            id="audit-entity"
            label="Record type"
            value={entityType}
            onChange={(value) => setEntityType(value ?? "")}
            allowDeselect={false}
            data={[
              { value: "", label: "Everything" },
              ...entityTypes.map((value) => ({ value, label: value })),
            ]}
          />
          <Text fz="sm" c="gray.6">
            Nothing here can be edited or removed — the table rejects both at
            the database.
          </Text>
        </Stack>
      </SectionCard>

      {!events && !error && <LoadingSkeleton variant="list" count={6} />}
      {events?.length === 0 && (
        <EmptyState title="Nothing recorded under that filter" />
      )}

      {events?.map((event) => {
        const payload = formatPayload(event.payload);
        return (
          <Stack key={event.id} gap={0}>
            <RecordCard
              primary={actionLabel(event.action)}
              secondary={
                event.actor ? `${event.actor.name} · ${event.actor.role}` : "System"
              }
              meta={`${new Date(event.createdAt).toLocaleString()} · ${event.entityType}`}
              action={
                payload ? (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() =>
                      setOpenId(openId === event.id ? undefined : event.id)
                    }
                  >
                    {openId === event.id ? "Hide" : "Details"}
                  </Button>
                ) : undefined
              }
            />
            {openId === event.id && payload && (
              /* Shown as stored rather than summarised: the question this
                 screen answers is usually "what exactly changed?", and a
                 summary is where that detail goes to die. */
              <Code block mt="xs" style={{ whiteSpace: "pre-wrap" }}>
                {payload}
              </Code>
            )}
          </Stack>
        );
      })}
    </Page>
  );
}
